/**
 * The site a meeting page is on. Chicago's dashboard and chicagopolitics.org carry no window.CITY, so every value here is
 * Chicago's; another city of the network serves the page with window.CITY (worker/src/city-site.js, its public settings):
 * its time zone, its name, what its members and seats are called, its member pages, and what it has (no accounts: no
 * Follow, notes or highlights; Ask is open to everyone there). Read when asked (window.CITY is set before the app runs).
 */
interface CityConfig {
  slug: string; siteName: string; timezone: string; canonicalHost: string;
  legislature: { name: string };
  member: { one: string; many: string; salutation?: string };
  district: { one: string; slug: string };
  seats: { districts: number; atLarge?: { seat: number; label: string }[] };
  features?: Record<string, boolean>;
}

/** Another city's settings, or null on Chicago's pages (and in the Worker). */
const other = (): CityConfig | null => {
  const c = (globalThis as { CITY?: CityConfig }).CITY;
  return c && c.slug !== "chicago" ? c : null;
};

export const site = {
  /** Another city's site (not Chicago's). */
  get city() { return !!other(); },
  get timeZone() { return other()?.timezone ?? "America/Chicago"; },
  get name() { return other()?.siteName ?? "Chicago Politics"; },
  get host() { return other()?.canonicalHost ?? "www.chicagopolitics.org"; },
  /** "alderpersons", "council members" (in a sentence). */
  get members() { return other()?.member.many.toLowerCase() ?? "alderpersons"; },
  /** "Alderpersons", "Councilors" (a heading or filter). */
  get Members() { return other()?.member.many ?? "Alderpersons"; },
  /** "ward", "district". */
  get seat() { return other()?.district.one.toLowerCase() ?? "ward"; },
  /** Follow, notes and highlights need an account; another city has none unless its settings say so. */
  get accounts() { const c = other(); return !c || !!c.features?.accounts; },
};

/** A seat as the page labels it: "Ward 12" (Chicago), "District 3", "At-Large", "Mayor". */
export function seatLabel(n: number): string {
  const c = other();
  if (!c) return `Ward ${n}`;
  return c.seats.atLarge?.find((a) => a.seat === n)?.label ?? `${c.district.one} ${n}`;
}

/** A sitting member's page: /ward/<n> (Chicago, wards 1–50), /district/<n> within the city's seats; none otherwise. */
export function seatPage(n: number): string | undefined {
  const c = other();
  const max = c ? c.seats.districts + (c.seats.atLarge?.length ?? 0) : 50;
  return n >= 1 && n <= max ? `/${c ? c.district.slug : "ward"}/${n}` : undefined;
}
